import { useState } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useCurrency } from "@/hooks/useCurrency";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { ArrowLeft, CreditCard } from "lucide-react";
import AppHeader from "@/components/AppHeader";

const VAT_RATE = 0.05;

interface LocationState {
  templateId: string;
  templateName: string;
  templateUrl: string;
  templatePrice: number;
  collectionId: string;
  occasion: string;
  recipientName: string;
}

const TemplateCheckout = () => {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const currency = useCurrency();
  const [paying, setPaying] = useState(false);

  const state = location.state as LocationState | null;

  if (!state) {
    navigate(`/card/${slug}/manage`, { replace: true });
    return null;
  }

  const { templateId, templateName, templateUrl, templatePrice, collectionId, occasion, recipientName } = state;

  const price = templatePrice;
  const vat = Math.round(price * VAT_RATE * 100) / 100;
  const total = Math.round((price + vat) * 100) / 100;

  const handlePay = async () => {
    setPaying(true);
    try {
      const { data: paymentData, error: paymentError } = await supabase.functions.invoke(
        "create-card-payment",
        {
          body: {
            collection_id: collectionId,
            template_id: templateId,
            success_redirect: `/card/${slug}/manage?upgrade_template=${templateId}`,
            cancel_redirect: `/card/${slug}/manage?payment=cancelled`,
          },
        }
      );
      if (paymentError || !paymentData?.url) {
        setPaying(false);
        return;
      }
      window.location.href = paymentData.url;
    } catch {
      setPaying(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <AppHeader userMenuVariant="dashboard" onBack={() => navigate(`/card/${slug}/manage`)} />

      <div className="max-w-md mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground font-serif">
            {t("checkout.title", { defaultValue: "Upgrade Design" })}
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            {t("checkout.subtitle", { defaultValue: "Review your order before payment" })}
          </p>
        </div>

        <div className="border border-border rounded-2xl overflow-hidden">
          {/* Template preview */}
          <div className="flex items-center gap-4 p-4 bg-muted/30">
            <img
              src={templateUrl}
              alt={templateName}
              className="w-20 h-28 rounded-lg object-cover border border-border shadow-sm"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground truncate">
                {templateName}
              </p>
              <p className="text-xs text-muted-foreground capitalize mt-0.5">{occasion?.replace(/_/g, " ")}</p>
              <p className="text-xs text-muted-foreground mt-0.5">
                {t("checkout.cardFor", { defaultValue: "Card for" })} {recipientName}
              </p>
            </div>
          </div>

          {/* Price breakdown */}
          <div className="p-4 space-y-3">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {t("wizard.checkout.designPrice", { defaultValue: "Card Design" })}
              </span>
              <span className="font-medium text-foreground">
                {currency} {price.toFixed(2)}
              </span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                {t("wizard.checkout.vat", { defaultValue: "VAT (5%)" })}
              </span>
              <span className="font-medium text-foreground">
                {currency} {vat.toFixed(2)}
              </span>
            </div>
            <Separator />
            <div className="flex justify-between text-base font-bold">
              <span className="text-foreground">
                {t("wizard.checkout.total", { defaultValue: "Total" })}
              </span>
              <span className="text-foreground">
                {currency} {total.toFixed(2)}
              </span>
            </div>
          </div>
        </div>

        <p className="text-xs text-muted-foreground text-center">
          {t("checkout.note", { defaultValue: "You'll be redirected to a secure payment page. Your card design will be updated after payment is completed." })}
        </p>

        <div className="flex flex-col gap-3">
          <Button
            onClick={handlePay}
            disabled={paying}
            className="w-full rounded-full bg-gradient-primary-animated text-primary-foreground shadow-lg shadow-primary/25 h-12 text-base"
            size="lg"
          >
            <CreditCard size={16} className="me-2" />
            {paying
              ? t("manage.redirecting", { defaultValue: "Redirecting…" })
              : t("checkout.pay", { defaultValue: `Pay ${currency} ${total.toFixed(2)}` })
            }
          </Button>
          <Button
            onClick={() => navigate(`/card/${slug}/manage`)}
            variant="outline"
            className="w-full rounded-full h-12 text-base"
            size="lg"
          >
            <ArrowLeft size={16} className="me-2" />
            {t("common.cancel", { defaultValue: "Cancel" })}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TemplateCheckout;
